import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IoAdd, IoChevronDown } from 'react-icons/io5';
import { getMonthsForAcademicYear } from '../../utils/monthToWeekMapping';
import { getWeekNumber } from '../../utils/helpers.ts';
import ClassSchedulerTimeFrameSelector from '../ClassSchedulerTimeFrameSelector';
import YearStructurePeriodEditor from '../YearStructurePeriodEditor';
import Modal from '../ui/Modal';
import PortaledMenu from '../ui/PortaledMenu';
import { getTimelineExpanded, setTimelineExpanded } from '../../utils/classSchedulerUiState';

const FALLBACK_COLORS = [
    '#2563EB',
    '#16A34A',
    '#EA580C',
    '#9333EA',
    '#0891B2',
    '#DB2777',
    '#65A30D',
    '#DC2626'
];

const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'short' });

const formatPeriodOptionLabel = (period) => {
    if (!period) return 'Period';

    const startWeek = period.start_week ?? period.startWeek;
    const endWeek = period.end_week ?? period.endWeek;
    if (startWeek != null && endWeek != null) {
        return `${period.name || 'Period'} (W${startWeek}-${endWeek})`;
    }

    return period.name || 'Period';
};

const parseAcademicYearStart = (academicYear) => {
    const match = String(academicYear || '').match(/^(\d{4})/);
    return match?.[1] ? Number(match[1]) : null;
};

const getPeriodYear = (period, primaryKey, fallbackKey) => {
    const raw = period?.[primaryKey] ?? period?.[fallbackKey];
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

const inferTimelineStartYear = (yearStructure, timePeriods) => {
    const academicYearStart = parseAcademicYearStart(yearStructure?.academicYear);
    if (academicYearStart) return academicYearStart;

    const periodYears = (timePeriods || [])
        .map((period) => getPeriodYear(period, 'start_week_year', 'startWeekYear') ?? getPeriodYear(period, 'year', 'year'))
        .filter(Number.isFinite);

    return periodYears.length ? Math.min(...periodYears) : new Date().getFullYear();
};

const buildWeekRange = (startWeek, endWeek, year) => {
    if (startWeek <= endWeek) {
        return Array.from({ length: endWeek - startWeek + 1 }, (_, index) => ({
            week: startWeek + index,
            year
        }));
    }

    return [
        ...Array.from({ length: 53 - startWeek + 1 }, (_, index) => ({
            week: startWeek + index,
            year
        })),
        ...Array.from({ length: endWeek }, (_, index) => ({
            week: index + 1,
            year: year + 1
        }))
    ];
};

const buildWeekSequence = (yearStructure, timePeriods) => {
    const start = Number(yearStructure?.startWeek ?? 1);
    const end = Number(yearStructure?.endWeek ?? 53);
    const startYear = inferTimelineStartYear(yearStructure, timePeriods);

    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 1 || start > 53 || end < 1 || end > 53) {
        return Array.from({ length: 53 }, (_, index) => ({ week: index + 1, year: startYear }));
    }

    if (start > end) {
        return buildWeekRange(start, end, startYear);
    }

    const explicitYears = (timePeriods || [])
        .flatMap((period) => [
            getPeriodYear(period, 'start_week_year', 'startWeekYear') ?? getPeriodYear(period, 'year', 'year'),
            getPeriodYear(period, 'end_week_year', 'endWeekYear') ?? getPeriodYear(period, 'year', 'year')
        ])
        .filter(Number.isFinite);
    const minYear = explicitYears.length ? Math.min(startYear, ...explicitYears) : startYear;
    const maxYear = explicitYears.length ? Math.max(startYear, ...explicitYears) : startYear;

    return Array.from({ length: maxYear - minYear + 1 }, (_, yearOffset) => minYear + yearOffset)
        .flatMap((year) => buildWeekRange(start, end, year));
};

const getPeriodWeek = (period, primaryKey, fallbackKey) => {
    const raw = period?.[primaryKey] ?? period?.[fallbackKey];
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

const getWeekIndex = (weekSequence, week, year) => weekSequence.findIndex((weekData) => {
    if (Number(weekData.week) !== Number(week)) return false;
    return year == null || Number(weekData.year) === Number(year);
});

const inferPeriodEndYear = (startWeek, endWeek, startYear) => {
    if (startYear == null) return null;
    return endWeek < startWeek ? startYear + 1 : startYear;
};

const normalizePeriodSegment = (period, index, weekSequence, totalWeeks) => {
    const startWeek = getPeriodWeek(period, 'start_week', 'startWeek');
    const endWeek = getPeriodWeek(period, 'end_week', 'endWeek');
    const startYear = getPeriodYear(period, 'start_week_year', 'startWeekYear') ?? getPeriodYear(period, 'year', 'year');
    const endYear = getPeriodYear(period, 'end_week_year', 'endWeekYear') ?? inferPeriodEndYear(startWeek, endWeek, startYear);
    const startIndex = getWeekIndex(weekSequence, startWeek, startYear);
    const endIndex = getWeekIndex(weekSequence, endWeek, endYear);

    if (startIndex === -1 || endIndex === -1) {
        return null;
    }

    const endExclusive = Math.max(endIndex + 1, startIndex + 1);
    const color = period.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length];

    return {
        period,
        start: startIndex,
        end: endExclusive,
        left: (startIndex / totalWeeks) * 100,
        width: Math.max(((endExclusive - startIndex) / totalWeeks) * 100, 1.5),
        color,
        label: period.name || `Period ${index + 1}`,
        startWeek,
        endWeek,
        startYear,
        endYear
    };
};

const getRangePosition = (weekSequence, totalWeeks, startWeek, startYear, endWeek, endYear) => {
    const startIndex = getWeekIndex(weekSequence, startWeek, startYear);
    const resolvedEndYear = endYear ?? inferPeriodEndYear(startWeek, endWeek, startYear);
    const endIndex = getWeekIndex(weekSequence, endWeek, resolvedEndYear);

    if (startIndex === -1 || endIndex === -1) {
        return null;
    }

    const endExclusive = Math.max(endIndex + 1, startIndex + 1);
    const left = (startIndex / totalWeeks) * 100;
    const right = Math.min((endExclusive / totalWeeks) * 100, 100);

    return {
        left,
        width: Math.max(right - left, 1),
        startMarkerLeft: left,
        endMarkerLeft: right
    };
};

const getRangeFromIndices = (startIndex, endIndexExclusive, totalWeeks) => {
    const left = (startIndex / totalWeeks) * 100;
    const right = Math.min((endIndexExclusive / totalWeeks) * 100, 100);
    return {
        left,
        width: Math.max(right - left, 1),
        startMarkerLeft: left,
        endMarkerLeft: right
    };
};

const getSelectedTimeFrameRange = ({
    selectedWeek,
    selectedMonth,
    selectedPeriodId,
    timelineViewRange,
    timePeriods,
    weekSequence,
    totalWeeks
}) => {
    if (timelineViewRange
        && typeof timelineViewRange.startWeek === 'number'
        && typeof timelineViewRange.endWeek === 'number') {
        const startWeek = timelineViewRange.startWeek;
        const endWeek = timelineViewRange.endWeek;
        const startYear = timelineViewRange.startWeekYear ?? null;
        const endYear = timelineViewRange.endWeekYear ?? null;

        return getRangePosition(
            weekSequence,
            totalWeeks,
            startWeek,
            startYear,
            endWeek,
            endYear
        );
    }

    if (selectedWeek && typeof selectedWeek.week === 'number') {
        return getRangePosition(
            weekSequence,
            totalWeeks,
            selectedWeek.week,
            selectedWeek.year,
            selectedWeek.week,
            selectedWeek.year
        );
    }

    if (selectedMonth?.weekRange && typeof selectedMonth.weekRange.start === 'number' && typeof selectedMonth.weekRange.end === 'number') {
        const startWeek = selectedMonth.weekRange.start;
        const endWeek = selectedMonth.weekRange.end;
        const startYear = selectedMonth.calendarYear;
        const endYear = endWeek < startWeek ? startYear + 1 : startYear;

        return getRangePosition(
            weekSequence,
            totalWeeks,
            startWeek,
            startYear,
            endWeek,
            endYear
        );
    }

    const selectedPeriod = (timePeriods || []).find((period) => String(period.id) === String(selectedPeriodId));
    if (!selectedPeriod) {
        return null;
    }

    const startWeek = getPeriodWeek(selectedPeriod, 'start_week', 'startWeek');
    const endWeek = getPeriodWeek(selectedPeriod, 'end_week', 'endWeek');
    const startYear = getPeriodYear(selectedPeriod, 'start_week_year', 'startWeekYear') ?? getPeriodYear(selectedPeriod, 'year', 'year');
    const endYear = getPeriodYear(selectedPeriod, 'end_week_year', 'endWeekYear') ?? inferPeriodEndYear(startWeek, endWeek, startYear);

    return getRangePosition(
        weekSequence,
        totalWeeks,
        startWeek,
        startYear,
        endWeek,
        endYear
    );
};

const stackSegments = (segments) => {
    const rows = [];

    segments
        .slice()
        .sort((a, b) => a.start - b.start || a.end - b.end)
        .forEach((segment) => {
            const row = rows.find((candidate) => candidate.every((placed) => segment.start >= placed.end || segment.end <= placed.start));
            if (row) {
                row.push(segment);
            } else {
                rows.push([segment]);
            }
        });

    return rows;
};

const getMonthWeekRangeIndices = (weekSequence, month) => {
    const range = month.weekRange || {};
    const startWeek = range.start;
    const endWeek = range.end;
    const startYear = month.calendarYear;

    if (typeof startWeek !== 'number' || typeof endWeek !== 'number' || startYear == null) {
        return null;
    }

    // January can start in week 52/53 of the previous ISO year (e.g. 1 Jan 2027 is 2026-W53).
    const wrapsIntoPreviousIsoYear = endWeek < startWeek && startWeek >= 52;
    const rangeStartYear = wrapsIntoPreviousIsoYear ? startYear - 1 : startYear;
    const rangeEndYear = endWeek < startWeek && !wrapsIntoPreviousIsoYear ? startYear + 1 : startYear;
    const startIndex = getWeekIndex(weekSequence, startWeek, rangeStartYear);
    const endIndex = getWeekIndex(weekSequence, endWeek, rangeEndYear);

    if (startIndex === -1 || endIndex === -1) {
        return null;
    }

    return {
        start: startIndex,
        end: Math.max(endIndex + 1, startIndex + 1)
    };
};

const getActiveTimelineWeekSequence = (yearStructure, weekSequence, segments) => {
    if (!Array.isArray(weekSequence) || weekSequence.length === 0 || !Array.isArray(segments) || segments.length === 0) {
        return weekSequence;
    }

    const activeMonthRanges = getMonthsForVisibleTimeline(yearStructure, weekSequence)
        .map((month) => getMonthWeekRangeIndices(weekSequence, month))
        .filter((monthRange) => monthRange && segments.some((segment) => (
            monthRange.start < segment.end && segment.start < monthRange.end
        )));

    if (activeMonthRanges.length === 0) {
        return weekSequence;
    }

    const start = Math.max(0, Math.min(
        ...activeMonthRanges.map((range) => range.start),
        ...segments.map((segment) => segment.start)
    ));
    const end = Math.min(
        weekSequence.length,
        Math.max(
            ...activeMonthRanges.map((range) => range.end),
            ...segments.map((segment) => segment.end)
        )
    );

    return start < end ? weekSequence.slice(start, end) : weekSequence;
};

const getVisibleYears = (weekSequence) => {
    return [...new Set((weekSequence || [])
        .map((weekData) => Number(weekData.year))
        .filter(Number.isFinite))]
        .sort((a, b) => a - b);
};

const getMonthsForVisibleTimeline = (yearStructure, weekSequence) => {
    const years = getVisibleYears(weekSequence);
    const academicStartWeek = Number(yearStructure?.startWeek ?? 1);
    const academicEndWeek = Number(yearStructure?.endWeek ?? 53);

    return years.flatMap((year) => getMonthsForAcademicYear({
        ...yearStructure,
        academicYear: `${year}-${year + 1}`,
        startWeek: academicStartWeek,
        endWeek: academicEndWeek
    }));
};

const addCalendarMonths = (date, months) => {
    const next = new Date(date.getTime());
    next.setMonth(next.getMonth() + months);
    return next;
};

const getIsoWeekYear = (date) => {
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    utcDate.setUTCDate(utcDate.getUTCDate() + 4 - (utcDate.getUTCDay() || 7));
    return utcDate.getUTCFullYear();
};

const dateToWeekEntry = (date) => ({
    week: getWeekNumber(date),
    year: getIsoWeekYear(date)
});

const weeksInIsoYear = (year) => getWeekNumber(new Date(year, 11, 28));

const getWeekSortKey = (week, year) => (Number(year) * 53) + Number(week);

const buildWeekSequenceBetweenDates = (startDate, endDate) => {
    const start = dateToWeekEntry(startDate);
    const end = dateToWeekEntry(endDate);
    const sequence = [];
    let year = start.year;
    let week = start.week;

    for (let index = 0; index < 200; index += 1) {
        sequence.push({ week, year });
        if (getWeekSortKey(week, year) >= getWeekSortKey(end.week, end.year)) {
            break;
        }

        week += 1;
        if (week > weeksInIsoYear(year)) {
            week = 1;
            year += 1;
        }
    }

    return sequence;
};

const findWeekIndexForDate = (weekSequence, date, { asEnd = false } = {}) => {
    if (!Array.isArray(weekSequence) || weekSequence.length === 0) {
        return 0;
    }

    const { week, year } = dateToWeekEntry(date);
    const exact = getWeekIndex(weekSequence, week, year);
    if (exact !== -1) {
        return asEnd ? exact + 1 : exact;
    }

    const target = getWeekSortKey(week, year);
    const first = getWeekSortKey(weekSequence[0].week, weekSequence[0].year);
    const last = getWeekSortKey(
        weekSequence[weekSequence.length - 1].week,
        weekSequence[weekSequence.length - 1].year
    );

    if (target < first) return 0;
    if (target > last) return weekSequence.length;

    let closestIndex = 0;
    let closestDistance = Infinity;
    weekSequence.forEach((entry, index) => {
        const distance = Math.abs(getWeekSortKey(entry.week, entry.year) - target);
        if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
        }
    });

    return asEnd ? closestIndex + 1 : closestIndex;
};

const getDefaultViewportRange = (weekSequence, segments) => {
    const total = weekSequence.length || 1;
    if (!Array.isArray(segments) || segments.length === 0) {
        return { startIndex: 0, endIndexExclusive: total };
    }

    const now = new Date();
    const minPeriodStart = Math.min(...segments.map((segment) => segment.start));
    const maxPeriodEnd = Math.max(...segments.map((segment) => segment.end));
    const minus3Index = findWeekIndexForDate(weekSequence, addCalendarMonths(now, -3));
    const plus1YearEnd = findWeekIndexForDate(weekSequence, addCalendarMonths(now, 12), { asEnd: true });

    let startIndex = Math.max(minus3Index, minPeriodStart);
    let endIndexExclusive = Math.min(plus1YearEnd, maxPeriodEnd);

    if (startIndex >= endIndexExclusive) {
        const preferredSpan = 65;
        if (minPeriodStart >= plus1YearEnd) {
            startIndex = minPeriodStart;
            endIndexExclusive = Math.min(maxPeriodEnd, startIndex + preferredSpan);
        } else {
            endIndexExclusive = maxPeriodEnd;
            startIndex = Math.max(minPeriodStart, endIndexExclusive - preferredSpan);
        }
    }

    startIndex = Math.max(0, Math.min(startIndex, total - 1));
    endIndexExclusive = Math.max(startIndex + 1, Math.min(total, endIndexExclusive));
    return { startIndex, endIndexExclusive };
};

const getMondayOfIsoWeek = (week, year) => {
    const januaryFourth = new Date(Date.UTC(year, 0, 4));
    const januaryFourthDay = januaryFourth.getUTCDay() || 7;
    const monday = new Date(januaryFourth);
    monday.setUTCDate(januaryFourth.getUTCDate() - (januaryFourthDay - 1) + ((week - 1) * 7));
    return monday;
};

const getMonthMarkersForWeekSequence = (weekSequence, totalWeeks) => {
    const seenMonths = new Set();

    return (weekSequence || [])
        .map((entry, index) => {
            const monday = getMondayOfIsoWeek(entry.week, entry.year);
            const thursday = new Date(monday);
            thursday.setUTCDate(monday.getUTCDate() + 3);
            const calendarYear = thursday.getUTCFullYear();
            const monthIndex = thursday.getUTCMonth();
            const key = `${calendarYear}-${monthIndex}`;

            if (seenMonths.has(key)) {
                return null;
            }

            seenMonths.add(key);

            return {
                key,
                label: MONTH_LABEL_FORMATTER.format(thursday),
                calendarYear,
                left: (index / totalWeeks) * 100
            };
        })
        .filter(Boolean);
};

const getTodayMarkerPosition = (weekSequence, totalWeeks) => {
    const now = new Date();
    const { week, year } = dateToWeekEntry(now);
    const index = getWeekIndex(weekSequence, week, year);

    if (index === -1) {
        return null;
    }

    return {
        left: (index / totalWeeks) * 100
    };
};

const TodayHereIcon = () => (
    <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="h-3.5 w-3.5 flex-none text-gray-400/90"
        fill="currentColor"
    >
        <path d="M10 2.25a4.25 4.25 0 0 0-4.25 4.25c0 2.95 3.35 6.65 3.95 7.35a.75.75 0 0 0 1.1 0c.6-.7 3.45-4.4 3.45-7.35A4.25 4.25 0 0 0 10 2.25Zm0 5.5a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Z" />
    </svg>
);

const PeriodVisibilityIcon = ({ selected }) => (
    <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="h-[1.05rem] w-[1.05rem] flex-none"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {selected ? (
            <>
                <path d="M2.5 10s2.8-4.5 7.5-4.5 7.5 4.5 7.5 4.5-2.8 4.5-7.5 4.5S2.5 10 2.5 10Z" />
                <circle cx="10" cy="10" r="2" />
            </>
        ) : (
            <>
                <path d="M3.5 8.5c1.7 2 3.8 3 6.5 3s4.8-1 6.5-3" />
                <path d="M6 11l-1 1.5" />
                <path d="M10 12v2" />
                <path d="M14 11l1 1.5" />
            </>
        )}
    </svg>
);

const PeriodTimelineChart = ({
    timePeriods = [],
    yearStructure,
    selectedPeriodId,
    selectedMonth,
    selectedWeek,
    onPeriodChange,
    onMonthChange,
    onWeekChange,
    currentView,
    periodSelectionDisabled = false,
    periodSelectionDisabledReason = '',
    visiblePeriodIds = null,
    onPeriodVisibilityToggle,
    timelineViewRange,
    onTimelineRangeManualChange,
    timeFrameRange,
    onTimeFrameRangeChange,
    setError,
    setSuccess,
    refreshTimePeriods
}) => {
    const [isExpanded, setIsExpanded] = useState(() => getTimelineExpanded(true));
    const [contextMenu, setContextMenu] = useState(null);
    const [editingPeriod, setEditingPeriod] = useState(null);
    const [isCreatingPeriod, setIsCreatingPeriod] = useState(false);
    const viewportRef = useRef(null);
    const appliedViewportFrameKeyRef = useRef(null);
    const trackRef = useRef(null);
    const [draggingHandle, setDraggingHandle] = useState(null);
    const [manualDragRange, setManualDragRange] = useState(null);
    const dragRangeRef = useRef(null);

    useEffect(() => {
        setTimelineExpanded(isExpanded);
    }, [isExpanded]);

    const fullWeekSequence = useMemo(() => (
        buildWeekSequence(yearStructure, timePeriods)
    ), [yearStructure, timePeriods]);
    const fullTotalWeeks = fullWeekSequence.length || 1;
    const fullPeriodSegments = useMemo(() => (
        timePeriods
            .map((period, index) => normalizePeriodSegment(period, index, fullWeekSequence, fullTotalWeeks))
            .filter(Boolean)
    ), [timePeriods, fullWeekSequence, fullTotalWeeks]);

    const weekSequence = useMemo(() => {
        if (!fullPeriodSegments.length) {
            const now = new Date();
            return buildWeekSequenceBetweenDates(addCalendarMonths(now, -3), addCalendarMonths(now, 6));
        }

        return getActiveTimelineWeekSequence(yearStructure, fullWeekSequence, fullPeriodSegments);
    }, [yearStructure, fullWeekSequence, fullPeriodSegments]);
    const totalWeeks = weekSequence.length || 1;

    const periodSegments = useMemo(() => (
        timePeriods
            .map((period, index) => normalizePeriodSegment(period, index, weekSequence, totalWeeks))
            .filter(Boolean)
    ), [timePeriods, weekSequence, totalWeeks]);

    const rows = useMemo(() => stackSegments(periodSegments), [periodSegments]);

    const monthMarkers = useMemo(
        () => getMonthMarkersForWeekSequence(weekSequence, totalWeeks),
        [weekSequence, totalWeeks]
    );

    const yearMarkers = useMemo(() => {
        const seenYears = new Set();

        return monthMarkers.filter((marker) => {
            if (seenYears.has(marker.calendarYear)) {
                return false;
            }

            seenYears.add(marker.calendarYear);
            return true;
        });
    }, [monthMarkers]);

    const selectedTimeFrameRange = useMemo(() => getSelectedTimeFrameRange({
        selectedWeek,
        selectedMonth,
        selectedPeriodId,
        timelineViewRange,
        timePeriods,
        weekSequence,
        totalWeeks
    }), [selectedWeek, selectedMonth, selectedPeriodId, timelineViewRange, timePeriods, weekSequence, totalWeeks]);

    const displayRange = manualDragRange
        ? getRangeFromIndices(manualDragRange.startIndex, manualDragRange.endIndexExclusive, totalWeeks)
        : selectedTimeFrameRange;

    const todayMarker = useMemo(
        () => getTodayMarkerPosition(weekSequence, totalWeeks),
        [weekSequence, totalWeeks]
    );

    const defaultViewportRange = useMemo(
        () => getDefaultViewportRange(weekSequence, periodSegments),
        [weekSequence, periodSegments]
    );
    const visibleWeekCount = Math.max(
        1,
        defaultViewportRange.endIndexExclusive - defaultViewportRange.startIndex
    );
    const timelineContentWidthPercent = (totalWeeks / visibleWeekCount) * 100;

    useLayoutEffect(() => {
        if (!isExpanded) {
            appliedViewportFrameKeyRef.current = null;
            return undefined;
        }

        const viewport = viewportRef.current;
        if (!viewport) return undefined;

        const frameKey = [
            defaultViewportRange.startIndex,
            defaultViewportRange.endIndexExclusive,
            totalWeeks
        ].join('-');
        const shouldApply = appliedViewportFrameKeyRef.current !== frameKey;
        if (!shouldApply) {
            return undefined;
        }

        appliedViewportFrameKeyRef.current = frameKey;
        const applyScroll = () => {
            viewport.scrollLeft = (defaultViewportRange.startIndex / visibleWeekCount) * viewport.clientWidth;
        };
        applyScroll();
        const rafId = requestAnimationFrame(applyScroll);
        return () => cancelAnimationFrame(rafId);
    }, [isExpanded, defaultViewportRange, totalWeeks, visibleWeekCount]);

    const visiblePeriodIdSet = useMemo(() => {
        if (visiblePeriodIds == null) {
            return selectedPeriodId != null ? new Set([String(selectedPeriodId)]) : new Set();
        }
        return new Set((visiblePeriodIds || []).map((id) => String(id)));
    }, [visiblePeriodIds, selectedPeriodId]);

    const canSelectPeriod = typeof onPeriodChange === 'function' && !periodSelectionDisabled;
    const collapsedPeriodSelectValue = selectedPeriodId != null
        ? String(selectedPeriodId)
        : String(timePeriods[0]?.id ?? '');

    const canManipulateRange = typeof onTimelineRangeManualChange === 'function';

    const baseRangeIndices = useMemo(() => {
        if (timelineViewRange && typeof timelineViewRange.startWeek === 'number' && typeof timelineViewRange.endWeek === 'number') {
            const startWeek = timelineViewRange.startWeek;
            const endWeek = timelineViewRange.endWeek;
            const startYear = timelineViewRange.startWeekYear ?? null;
            const endYear = timelineViewRange.endWeekYear ?? inferPeriodEndYear(startWeek, endWeek, startYear) ?? null;
            const startIndex = getWeekIndex(weekSequence, startWeek, startYear);
            const endIndex = getWeekIndex(weekSequence, endWeek, endYear);
            if (startIndex !== -1 && endIndex !== -1) {
                return { startIndex, endIndexExclusive: Math.max(endIndex + 1, startIndex + 1) };
            }
            return null;
        }

        if (selectedWeek && typeof selectedWeek.week === 'number') {
            const idx = getWeekIndex(weekSequence, selectedWeek.week, selectedWeek.year);
            if (idx !== -1) return { startIndex: idx, endIndexExclusive: idx + 1 };
            return null;
        }

        if (selectedMonth?.weekRange) {
            const range = getMonthWeekRangeIndices(weekSequence, selectedMonth);
            if (range) return { startIndex: range.start, endIndexExclusive: range.end };
            return null;
        }

        const selectedPeriod = (timePeriods || []).find((period) => String(period.id) === String(selectedPeriodId));
        if (selectedPeriod) {
            const startWeek = getPeriodWeek(selectedPeriod, 'start_week', 'startWeek');
            const endWeek = getPeriodWeek(selectedPeriod, 'end_week', 'endWeek');
            const startYear = getPeriodYear(selectedPeriod, 'start_week_year', 'startWeekYear') ?? getPeriodYear(selectedPeriod, 'year', 'year');
            const endYear = getPeriodYear(selectedPeriod, 'end_week_year', 'endWeekYear') ?? inferPeriodEndYear(startWeek, endWeek, startYear);
            const startIndex = getWeekIndex(weekSequence, startWeek, startYear);
            const endIndex = getWeekIndex(weekSequence, endWeek, endYear);
            if (startIndex !== -1 && endIndex !== -1) {
                return { startIndex, endIndexExclusive: Math.max(endIndex + 1, startIndex + 1) };
            }
        }

        return null;
    }, [timelineViewRange, selectedWeek, selectedMonth, selectedPeriodId, timePeriods, weekSequence]);

    const emitManualRange = useCallback((startIndex, endIndexExclusive) => {
        if (typeof onTimelineRangeManualChange !== 'function') return;
        const safeEndIndex = Math.max(endIndexExclusive - 1, startIndex);
        const startEntry = weekSequence[startIndex];
        const endEntry = weekSequence[safeEndIndex];
        if (!startEntry || !endEntry) return;
        onTimelineRangeManualChange({
            startWeek: startEntry.week,
            startWeekYear: startEntry.year,
            endWeek: endEntry.week,
            endWeekYear: endEntry.year
        });
    }, [onTimelineRangeManualChange, weekSequence]);

    const handleDragMove = useCallback((clientX) => {
        if (!draggingHandle || !trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        if (rect.width <= 0) return;

        const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const rawIndex = Math.round(fraction * totalWeeks);

        const prev = dragRangeRef.current;
        if (!prev) return;

        const next = draggingHandle === 'start'
            ? { startIndex: Math.max(0, Math.min(rawIndex, prev.endIndexExclusive - 1)), endIndexExclusive: prev.endIndexExclusive }
            : { startIndex: prev.startIndex, endIndexExclusive: Math.max(rawIndex, prev.startIndex + 1) };

        dragRangeRef.current = next;
        setManualDragRange(next);
        emitManualRange(next.startIndex, next.endIndexExclusive);
    }, [draggingHandle, totalWeeks, emitManualRange]);

    useEffect(() => {
        if (!draggingHandle) return undefined;

        const handlePointerMove = (event) => handleDragMove(event.clientX);
        const handlePointerUp = () => {
            setDraggingHandle(null);
            setManualDragRange(null);
            dragRangeRef.current = null;
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };
    }, [draggingHandle, handleDragMove]);

    const handleHandlePointerDown = (side, event) => {
        if (!canManipulateRange) return;
        if (!baseRangeIndices) return;
        event.preventDefault();
        event.stopPropagation();
        dragRangeRef.current = baseRangeIndices;
        setManualDragRange(baseRangeIndices);
        setDraggingHandle(side);
    };

    const handleCollapsedPeriodChange = (event) => {
        if (!canSelectPeriod) return;

        const nextPeriod = timePeriods.find((period) => String(period.id) === String(event.target.value));
        if (nextPeriod) {
            onPeriodChange(nextPeriod.id, nextPeriod);
        }
    };

    const openContextMenu = (event, extra = {}) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            ...extra
        });
    };

    const handleSegmentContextMenu = (event, segment) => {
        openContextMenu(event, { type: 'segment', segment });
    };

    const handleBackgroundContextMenu = (event) => {
        openContextMenu(event, { type: 'background' });
    };

    const handleSetSelectedFromMenu = () => {
        const segment = contextMenu?.segment;
        setContextMenu(null);
        if (!segment || !canSelectPeriod) return;
        onPeriodChange(segment.period.id, segment.period);
    };

    const handleEditFromMenu = () => {
        const segment = contextMenu?.segment;
        setContextMenu(null);
        if (!segment) return;
        setIsCreatingPeriod(false);
        setEditingPeriod(segment.period);
    };

    const handleAddPeriodFromMenu = () => {
        setContextMenu(null);
        setEditingPeriod(null);
        setIsCreatingPeriod(true);
    };

    const canToggleVisibility = typeof onPeriodVisibilityToggle === 'function';

    const handleSegmentVisibilityToggle = (event, segment) => {
        event.preventDefault();
        event.stopPropagation();
        if (!canToggleVisibility) return;
        onPeriodVisibilityToggle(segment.period.id);
    };

    const closePeriodEditor = () => {
        setEditingPeriod(null);
        setIsCreatingPeriod(false);
    };

    const handlePeriodEditorSuccess = async ({ period }) => {
        const created = editingPeriod == null;
        if (refreshTimePeriods) {
            await refreshTimePeriods();
        }
        closePeriodEditor();
        if (created && period?.id != null && canSelectPeriod) {
            onPeriodChange(period.id, period);
        }
        if (setSuccess) {
            const label = period?.name ? `"${period.name}"` : 'Period';
            setSuccess(created ? `${label} added successfully.` : `${label} updated successfully.`);
            setTimeout(() => setSuccess(null), 3000);
        }
    };

    const hasTimelineRows = rows.length > 0;

    return (
        <div className={`flex-none px-4 bg-white border-b border-gray-200 ${isExpanded ? 'pb-3' : 'pb-2'}`}>
            <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                <div className={`flex items-center gap-2 px-3 pt-2 ${isExpanded ? 'pb-0' : 'pb-2'}`}>
                    <div className="min-w-0 flex-1">
                        <ClassSchedulerTimeFrameSelector
                            timePeriods={timePeriods}
                            selectedPeriodId={selectedPeriodId}
                            onMonthChange={onMonthChange}
                            onWeekChange={onWeekChange}
                            selectedWeek={selectedWeek}
                            selectedMonth={selectedMonth}
                            currentView={currentView}
                            selectedRange={timeFrameRange}
                            onRangeChange={onTimeFrameRangeChange}
                            collapsedPeriodSelect={!isExpanded ? (
                                <div className="relative w-auto">
                                    <select
                                        value={collapsedPeriodSelectValue}
                                        onChange={handleCollapsedPeriodChange}
                                        disabled={!canSelectPeriod}
                                        title={periodSelectionDisabled ? periodSelectionDisabledReason : undefined}
                                        aria-label="Select period"
                                        className="w-auto min-w-[10rem] max-w-[14rem] appearance-none rounded-lg border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm font-medium text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {timePeriods.map((period, index) => (
                                            <option key={period.id ?? `period-${index}`} value={String(period.id)}>
                                                {formatPeriodOptionLabel(period)}
                                            </option>
                                        ))}
                                    </select>
                                    <IoChevronDown
                                        className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500"
                                        aria-hidden="true"
                                    />
                                </div>
                            ) : null}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setIsExpanded((prev) => !prev)}
                        className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse period timeline' : 'Expand period timeline'}
                        title={isExpanded ? 'Collapse timeline' : 'Expand timeline'}
                    >
                        <IoChevronDown
                            className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            aria-hidden="true"
                        />
                    </button>
                </div>
                {isExpanded && (
                    <div
                        ref={viewportRef}
                        className="w-full min-w-0 overflow-x-auto overscroll-x-contain"
                        style={{ scrollbarWidth: 'thin' }}
                        aria-label="Schedule period timeline"
                    >
                        <div
                            className="relative px-4 pt-8 pb-3"
                            style={{ width: `${timelineContentWidthPercent}%`, minWidth: '100%' }}
                            onContextMenu={handleBackgroundContextMenu}
                        >
                        <div className="absolute inset-x-4 top-1 h-4">
                            {yearMarkers.map((marker) => (
                                <div
                                    key={`${marker.calendarYear}-year`}
                                    className="absolute top-0 -translate-x-1/2 text-[10px] font-semibold text-gray-700"
                                    style={{ left: `${marker.left}%` }}
                                >
                                    {marker.calendarYear}
                                </div>
                            ))}
                        </div>

                        <div className="absolute inset-x-4 top-5 h-4">
                            {monthMarkers.map((marker) => (
                                <div
                                    key={marker.key}
                                    className="absolute top-0 -translate-x-1/2 text-[10px] font-medium text-gray-500"
                                    style={{ left: `${marker.left}%` }}
                                >
                                    {marker.label}
                                </div>
                            ))}
                        </div>

                        {todayMarker && (
                            <div className="absolute inset-x-4 top-6 h-4 pointer-events-none z-10">
                                <div
                                    className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
                                    style={{ left: `${todayMarker.left}%` }}
                                    title="Today"
                                    aria-label="Today"
                                >
                                    <TodayHereIcon />
                                </div>
                            </div>
                        )}

                        <div ref={trackRef} className="absolute inset-x-4 top-9 bottom-3 pointer-events-none z-20">
                            {displayRange && (
                                <>
                                    <div
                                        className="absolute top-0 bottom-0 border-l border-r border-blue-500 bg-blue-500/10"
                                        style={{
                                            left: `${displayRange.left}%`,
                                            width: `${displayRange.width}%`
                                        }}
                                    />
                                    <div
                                        className="absolute top-0 bottom-0 w-px bg-blue-500 pointer-events-none"
                                        style={{ left: `${displayRange.startMarkerLeft}%` }}
                                    />
                                    <div
                                        role="slider"
                                        tabIndex={canManipulateRange ? 0 : -1}
                                        aria-label="Start of selected timeframe"
                                        aria-valuemin={1}
                                        aria-valuemax={totalWeeks}
                                        aria-valuenow={Math.max(1, Math.round((displayRange.startMarkerLeft / 100) * totalWeeks))}
                                        onPointerDown={(event) => handleHandlePointerDown('start', event)}
                                        className={`group absolute top-0 h-6 w-4 -translate-x-1/2 pointer-events-auto ${canManipulateRange ? 'cursor-ew-resize touch-none' : ''}`}
                                        style={{ left: `${displayRange.startMarkerLeft}%` }}
                                    >
                                        <div className="absolute top-1/2 left-1/2 h-5 w-2 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-blue-500 bg-white shadow-sm transition-transform duration-150 group-hover:scale-125 group-hover:border-blue-600 group-active:scale-110" />
                                    </div>
                                    <div
                                        className="absolute top-0 bottom-0 w-0.5 bg-blue-500 pointer-events-none"
                                        style={{ left: `${displayRange.endMarkerLeft}%` }}
                                    />
                                    <div
                                        role="slider"
                                        tabIndex={canManipulateRange ? 0 : -1}
                                        aria-label="End of selected timeframe"
                                        aria-valuemin={1}
                                        aria-valuemax={totalWeeks}
                                        aria-valuenow={Math.max(1, Math.round((displayRange.endMarkerLeft / 100) * totalWeeks))}
                                        onPointerDown={(event) => handleHandlePointerDown('end', event)}
                                        className={`group absolute top-0 h-6 w-4 -translate-x-1/2 pointer-events-auto ${canManipulateRange ? 'cursor-ew-resize touch-none' : ''}`}
                                        style={{ left: `${displayRange.endMarkerLeft}%` }}
                                    >
                                        <div className="absolute top-1/2 left-1/2 h-5 w-2 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-blue-500 bg-white shadow-sm transition-transform duration-150 group-hover:scale-125 group-hover:border-blue-600 group-active:scale-110" />
                                    </div>
                                </>
                            )}
                            {monthMarkers.map((marker) => (
                                <div
                                    key={`${marker.key}-line`}
                                    className="absolute top-0 bottom-0 w-px bg-gray-200"
                                    style={{ left: `${marker.left}%` }}
                                />
                            ))}
                        </div>

                        <div className="relative space-y-1.5 pt-7">
                            {!hasTimelineRows && (
                                <div className="relative h-6">
                                    <div className="absolute inset-x-0 top-1/2 h-px bg-gray-200" />
                                </div>
                            )}
                            {rows.map((row, rowIndex) => (
                                <div key={rowIndex} className="relative h-6">
                                    <div className="absolute inset-x-0 top-1/2 h-px bg-gray-200" />
                                    {row.map((segment) => {
                                        const isSelected = String(segment.period.id) === String(selectedPeriodId);
                                        const isVisible = visiblePeriodIdSet.has(String(segment.period.id));
                                        const canSelect = typeof onPeriodChange === 'function';
                                        const stateClasses = isSelected
                                            ? 'z-10 opacity-100 brightness-110 shadow-[0_4px_10px_rgba(0,0,0,0.4)]'
                                            : 'opacity-80 hover:opacity-100 shadow-sm';

                                        return (
                                            <button
                                                key={segment.period.id ?? `${segment.label}-${segment.start}-${segment.end}`}
                                                type="button"
                                                onClick={() => {
                                                    if (canSelect) {
                                                        onPeriodChange(segment.period.id, segment.period);
                                                    }
                                                }}
                                                onContextMenu={(event) => handleSegmentContextMenu(event, segment)}
                                                aria-pressed={isSelected}
                                                aria-disabled={!canSelect || periodSelectionDisabled}
                                                title={periodSelectionDisabled ? periodSelectionDisabledReason : (canSelect ? 'View period' : segment.label)}
                                                className={`absolute top-0 h-6 px-2 text-left text-xs font-medium text-white transition-all ${canSelect ? 'hover:brightness-95 cursor-pointer' : 'cursor-default'} ${stateClasses}`}
                                                style={{
                                                    left: `${segment.left}%`,
                                                    width: `${segment.width}%`,
                                                    backgroundColor: segment.color
                                                }}
                                            >
                                                <span className="inline-flex max-w-full items-center gap-1.5">
                                                    <span className="block truncate">{segment.label}</span>
                                                    <span
                                                        role="button"
                                                        tabIndex={-1}
                                                        onClick={(event) => handleSegmentVisibilityToggle(event, segment)}
                                                        aria-label={isVisible
                                                            ? `Hide ${segment.label} from the schedule`
                                                            : `Show ${segment.label} alongside the current period`}
                                                        title={isVisible
                                                            ? 'Hide this period from the schedule'
                                                            : 'Show this period alongside the current period'}
                                                        className={`${canToggleVisibility ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'} flex flex-none items-center rounded p-0.5 transition-colors ${isVisible ? 'text-white' : 'text-white/70 hover:text-white'}`}
                                                    >
                                                        <PeriodVisibilityIcon selected={isVisible} />
                                                    </span>
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                        </div>
                    </div>
                )}
                {isExpanded && (
                    <button
                        type="button"
                        onClick={() => {
                            setEditingPeriod(null);
                            setIsCreatingPeriod(true);
                        }}
                        className="absolute bottom-1 right-1 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800"
                        aria-label="Add schedule period"
                        title="Add period"
                    >
                        <IoAdd className="h-5 w-5" />
                    </button>
                )}
            </div>

            <PortaledMenu
                isOpen={contextMenu != null}
                onClose={() => setContextMenu(null)}
                position={contextMenu}
                className="w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
            >
                {contextMenu?.type === 'background' || !contextMenu?.segment ? (
                    <button
                        type="button"
                        onClick={handleAddPeriodFromMenu}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                        Add New Period...
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={handleSetSelectedFromMenu}
                            disabled={!canSelectPeriod}
                            title={periodSelectionDisabled ? periodSelectionDisabledReason : undefined}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Set Selected
                        </button>
                        <button
                            type="button"
                            onClick={handleEditFromMenu}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                            Edit...
                        </button>
                    </>
                )}
            </PortaledMenu>

            <Modal
                isOpen={isCreatingPeriod || editingPeriod != null}
                onClose={closePeriodEditor}
                title={editingPeriod ? 'Edit Period' : 'Add Period'}
            >
                <YearStructurePeriodEditor
                    key={editingPeriod?.id ?? 'create'}
                    period={editingPeriod}
                    setError={setError}
                    onSuccess={handlePeriodEditorSuccess}
                    onCancel={closePeriodEditor}
                />
            </Modal>
        </div>
    );
};

export default PeriodTimelineChart;
